import { useRef, useState } from "react";
import { useSendToWhatsApp } from "./useSendToWhatsApp";
import { useApi } from "./useApi";
import { COMPANY_KEY } from "../config/company";
import { clearCheckoutKey, getCheckoutKey } from "../lib/orderIdempotency";

const formatBRL = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
};

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

/**
 * Hook customizado para criar pedidos e enviar para WhatsApp
 * Encapsula toda a lógica de criação de pedidos
 */
export const useCreateOrder = () => {
  const [loading, setLoading] = useState(false);
  const { sendToWhatsApp: sendToWhatsAppMessage } = useSendToWhatsApp();
  const api = useApi();
  const orderInFlight = useRef(false);

  /**
   * Cria o pedido no backend e retorna os dados do pedido criado.
   * Retorna null, sem fazer nada, se já houver um envio em andamento.
   */
  const createOrder = async (...args) => {
    if (orderInFlight.current) return null;
    orderInFlight.current = true;
    try {
      return await submitOrder(...args);
    } finally {
      orderInFlight.current = false;
    }
  };

  const submitOrder = async (
    name,
    phoneDigits,
    deliveryType,
    address,
    paymentMethod,
    cart,
    getTotalPrice,
    freightData = null, // Objeto completo do frete (null para retirada)
    couponCode = null // Código do cupom de desconto (null se não houver)
  ) => {
    const isDelivery = deliveryType === "delivery";
    const cepDigits = onlyDigits(address.cep);
    const payload = JSON.stringify({
      client: {
        code: "", // Nunca null: há NPE conhecido no back (docs/api.md 2.3)
        name: name,
        contact: phoneDigits,
        whatsapp: true,
        // AddressDTO (nomes em PT) — endereço do cadastro do cliente
        address: {
          cep: cepDigits,
          number: address.numero,
          complemento: address.complemento,
          logradouro: address.logradouro,
          bairro: address.bairro,
          localidade: address.localidade || "",
          uf: address.uf,
          estado: address.estado,
        },
      },
      // AddressUSDTO — endereço de entrega do pedido (omitido na retirada)
      ...(isDelivery && {
        address: {
          zipCode: cepDigits,
          number: address.numero,
          street: address.logradouro,
          complement: address.complemento || "",
          neighborhood: address.bairro,
          city: address.localidade || "",
          uf: address.uf,
          principal: true,
        },
      }),
      delivery_type: isDelivery ? "Delivery" : "Retirada",
      company_code: COMPANY_KEY,
      pay_method: paymentMethod,
      freight_value:
        isDelivery && freightData?.available
          ? Number(freightData.value || 0)
          : 0,
      freight_calculation_id:
        isDelivery && freightData?.available
          ? Number(freightData.calculationId || 0)
          : null,
      coupon_code: couponCode || null, // Adiciona o código do cupom
      products: cart.map((i) => {
        const allIngredients = Array.isArray(i.selected_ingredients)
          ? i.selected_ingredients
          : [];
        const allProdutos = Array.isArray(i.selected_produtos)
          ? i.selected_produtos
          : [];

        // Separa base de extras usando a flag isExtra
        const selectedIngredients = allIngredients
          .filter((ing) => !ing.isExtra && ing.qty > 0)
          .map(({ isExtra, ...rest }) => rest); // Remove a flag isExtra

        // Sub-produtos são identificados pelo code UUID (no carrinho fica em `id`)
        const selectedProdutos = allProdutos
          .filter((p) => !p.isExtra && p.qty > 0)
          .map(({ isExtra, ...rest }) => ({ ...rest, code: rest.code ?? rest.id })); // Remove a flag isExtra

        const selectedExtraIngredients = allIngredients.filter(
          (ing) => ing.isExtra === true && ing.qty > 0
        );

        const selectedExtraProdutos = allProdutos
          .filter((p) => p.isExtra === true && p.qty > 0)
          .map((p) => ({ ...p, code: p.code ?? p.id }));

        return {
          id: String(i.product_id),
          quantity: i.quantity,
          total_value: i.product_price * i.quantity,
          observation: i.comments,
          selected_ingredients: selectedIngredients,
          selected_produtos: selectedProdutos,
          selectedExtraIngredients,
          selectedExtraProdutos,
        };
      }),
    });

    try {
      // Não inclua campos variáveis (timestamp, id aleatório) no payload: a API compara
      // o corpo byte a byte e cada tentativa pareceria um pedido novo.
      const data = await api.post("/orders", JSON.parse(payload), {
        headers: { "Idempotency-Key": getCheckoutKey(payload) },
      });
      const created = data?.data?.[0];
      const codigo = created?.code;

      // Sem código confirmado a chave fica: a nova tentativa recupera o mesmo pedido
      if (codigo) clearCheckoutKey();

      return {
        data,
        codigo,
        address,
      };
    } catch (e) {
      console.error("Erro ao criar pedido:", e);

      // Mesma chave com corpo diferente: não há como reaproveitá-la
      if (e.code === "ERRC0130") {
        clearCheckoutKey();
      }

      // Monta mensagem de erro detalhada
      let errorMessage = "Não foi possível criar seu pedido.";

      if (e.data?.message) {
        errorMessage = e.data.message;
      } else if (e.message) {
        errorMessage = e.message;
      }

      if (!e.status) {
        // Falha de rede: o pedido pode ou não existir, e a chave fica para a nova tentativa
        errorMessage =
          "Não foi possível confirmar seu pedido. Verifique sua conexão e tente novamente.";
      } else {
        // Adiciona informações do status se disponível
        if (e.status === 404) {
          errorMessage =
            "Serviço temporariamente indisponível. Por favor, tente novamente em alguns instantes.";
        } else if (e.status >= 500) {
          errorMessage =
            "Erro no servidor. Por favor, tente novamente mais tarde.";
        } else if (e.code === "ERRC0130") {
          errorMessage =
            "Os dados do pedido mudaram. Revise as informações e confirme novamente.";
        } else if (e.status === 400) {
          errorMessage =
            e.data?.message ||
            "Dados inválidos. Verifique as informações e tente novamente.";
        }
      }

      const error = new Error(errorMessage);
      error.status = e.status;
      error.code = e.code;
      error.data = e.data;
      throw error;
    }
  };

  /**
   * Envia o pedido para WhatsApp (abre o link do WhatsApp Web)
   * Esta função cria o pedido no backend antes de enviar
   */
  const sendToWhatsApp = async (
    name,
    phoneDigits,
    deliveryType,
    address,
    paymentMethod,
    paymentMethodSelected,
    cart,
    getTotalPrice,
    onSuccess,
    freightData = null, // Objeto completo do frete
    couponCode = null // Código do cupom de desconto
  ) => {
    try {
      setLoading(true);

      // Valida os dados
      if (name.trim().length <= 3) {
        throw new Error("Por favor, informe um nome com mais de 3 caracteres.");
      }
      if (phoneDigits.length < 11) {
        throw new Error(
          "Por favor, informe um número de celular válido. (Ex: (xx) 9xxxx-xxxx)."
        );
      }

      // Cria o pedido
      const result = await createOrder(
        name,
        phoneDigits,
        deliveryType,
        address,
        paymentMethod,
        cart,
        getTotalPrice,
        freightData, // Passa objeto completo do frete
        couponCode // Passa código do cupom
      );

      // Clique repetido: o envio em andamento cuida do restante
      if (!result) return null;
      const { codigo } = result;

      // Salva no localStorage
      localStorage.setItem("pedido.codigo", String(codigo));
      localStorage.setItem(`pedido.status.${codigo}`, "0");
      localStorage.setItem("pedido.delivery", String(0));

      // Envia para WhatsApp usando o hook específico
      sendToWhatsAppMessage(
        codigo,
        name,
        phoneDigits,
        deliveryType,
        address,
        cart,
        getTotalPrice,
        paymentMethodSelected
      );

      // Chama callback de sucesso
      if (onSuccess) {
        onSuccess(codigo);
      }

      return { codigo };
    } catch (error) {
      console.error("Erro ao enviar para WhatsApp:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Envia apenas para WhatsApp sem criar novo pedido
   * Usa o código do pedido já criado anteriormente
   */
  const sendOnlyToWhatsApp = async (
    name,
    phoneDigits,
    deliveryType,
    address,
    cart,
    getTotalPrice,
    paymentMethodSelected,
    freightData = null // Adiciona freightData
  ) => {
    try {
      setLoading(true);

      // Recupera o código do pedido do localStorage
      const codigo = localStorage.getItem("pedido.codigo") || "0";

      // Envia para WhatsApp usando o hook específico
      sendToWhatsAppMessage(
        codigo,
        name,
        phoneDigits,
        deliveryType,
        address,
        cart,
        getTotalPrice,
        paymentMethodSelected,
        freightData // Passa freightData
      );

      return { codigo };
    } catch (error) {
      console.error("Erro ao enviar para WhatsApp:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createOrder,
    sendToWhatsApp,
    sendOnlyToWhatsApp,
  };
};
